import type {
    IStekzSchema,
    IStekzFieldProperties,
    IPolymorphicStekzSchemaBase,
} from "@/wc/registry/lib/service/repository/types";
import { get as lodashGet } from "lodash";

function getFieldPropertiesForPolymorphicType(
    schema: IPolymorphicStekzSchemaBase,
    identifier: string
): IStekzSchema {
    const discriminator = schema.discriminator.propertyName;
    return schema.oneOf.find(
        (fieldProperties) =>
            fieldProperties[discriminator].default === identifier
    );
}

// A fieldName can describe a nested field, e.g. producer.contact_name. This
// function extracts the properties for the field, nested or not.
export function getFieldProperties(
    fieldName: string,
    properties: IStekzSchema | IStekzFieldProperties | undefined,
    identifier?: string
): IStekzFieldProperties | IStekzSchema {
    if (properties === undefined) {
        return;
    }

    let fieldProperties: IStekzSchema | IStekzFieldProperties = properties;

    const shouldGetSchemaFromPolymorphicSchema =
        "oneOf" in fieldProperties && "discriminator" in fieldProperties;
    const shouldGetSpecificSchemaFromPolymorphicSchema =
        shouldGetSchemaFromPolymorphicSchema && identifier;
    // When the field is a polymorphic type and the identifier is given to
    // differentiate between these schemas, figure out which one of the schemas
    // we need to use.
    if (shouldGetSpecificSchemaFromPolymorphicSchema) {
        fieldProperties = getFieldPropertiesForPolymorphicType(
            fieldProperties as IPolymorphicStekzSchemaBase,
            identifier
        );

        // When no identifier is given, we can still figure out which schema we
        // need to use, but chances are that there are multiple schemas that contain
        // the properties for the field we are looking for. In that case, at least
        // make sure that the types for the fields of those schemas are the same.
    } else if (shouldGetSchemaFromPolymorphicSchema) {
        const fieldPropertiesForEachSchema: (
            | IStekzFieldProperties
            | IStekzSchema
        )[] = (fieldProperties as IPolymorphicStekzSchemaBase).oneOf
            .map((schema) => getFieldProperties(fieldName, schema))
            .filter((properties) => properties !== undefined);

        // When there are no properties for this field in any of the polymorphic
        // schemas.
        if (fieldPropertiesForEachSchema.length === 0) {
            return;
        }

        // When there are schemas that contain the properties for this field,
        // at least make sure that all of these properties are similar across
        // the related schemas.
        const hasMatchingTypes = fieldPropertiesForEachSchema.every(
            (fieldProperties) =>
                fieldProperties.type === fieldPropertiesForEachSchema[0].type
        );
        if (!hasMatchingTypes) {
            return;
        }

        return fieldPropertiesForEachSchema[0];
    }

    // When nested, we should navigate to properties using
    // fieldName.properties.nestedFieldName instead of fieldName.nestedFieldName.
    const fieldLocator = fieldName.split(".").join(".properties.");
    // Now get the properties from fieldName.properties.fieldName.
    return lodashGet(fieldProperties, fieldLocator);
}
